import { useEffect, useRef, type MouseEvent, type ReactNode } from 'react';

import { parseClassnames } from '@incloodsolutions/react-toolkit';

import { CloseIcon } from '@workinance/shared/ui/icons';

type ModalSize = 'sm' | 'md' | 'lg';

interface IModalLayoutComponentProps {
	isOpen: boolean;
	onClose: () => void;
	title: string;
	description?: string;
	size?: ModalSize;
	/** When false, Escape and a backdrop click do nothing; only the header's close button calls `onClose`. For content that would be lost on an accidental close. Defaults to true. */
	isDismissible?: boolean;
	/** Hides the header's close button, for a modal with no manual way to close it at all (it closes itself once some external condition changes). Defaults to true. */
	showCloseButton?: boolean;
	children: ReactNode;
}

const sizeClasses: Record<ModalSize, string> = {
	sm: 'max-w-sm',
	md: 'max-w-lg',
	lg: 'max-w-2xl',
};

/**
 * The standard modal dialog for all Workinance apps. Built on the native <dialog> element, so
 * focus trapping, Escape-to-close and the backdrop come from the browser. Closes on Escape, on a
 * backdrop click and from the header's close button, always through `onClose`.
 */
export function ModalLayoutComponent({
	isOpen,
	onClose,
	title,
	description,
	size = 'md',
	isDismissible = true,
	showCloseButton = true,
	children,
}: IModalLayoutComponentProps) {
	const dialogRef = useRef<HTMLDialogElement>(null);

	useEffect(() => {
		const dialogElement = dialogRef.current;
		if (!dialogElement) return;

		if (isOpen && !dialogElement.open) dialogElement.showModal();
		if (!isOpen && dialogElement.open) dialogElement.close();
	}, [isOpen]);

	// The page behind a modal must not scroll while it is open.
	useEffect(() => {
		if (!isOpen) return;

		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		return () => {
			document.body.style.overflow = previousOverflow;
		};
	}, [isOpen]);

	const handleDialogClick = (event: MouseEvent<HTMLDialogElement>) => {
		// The dialog element itself is only the click target when the backdrop is clicked.
		if (isDismissible && event.target === dialogRef.current) onClose();
	};

	return (
		<dialog
			ref={dialogRef}
			onClose={onClose}
			onCancel={(event) => {
				// Escape: keep the dialog open instead of letting the browser close it.
				if (!isDismissible) event.preventDefault();
			}}
			onClick={handleDialogClick}
			aria-labelledby="modal-title"
			className={parseClassnames(
				'm-auto max-h-[90vh] w-[calc(100%-2rem)] overflow-y-auto rounded-[28px] border border-rule bg-panel p-0 text-ink shadow-xl backdrop:bg-overlay',
				sizeClasses[size],
			)}
		>
			<div className="flex items-start justify-between gap-4 border-b border-rule px-6 py-5">
				<div>
					<h2
						id="modal-title"
						className="text-2xl font-bold tracking-[-0.03em]"
					>
						{title}
					</h2>
					{description ? <p className="mt-1 text-mute">{description}</p> : null}
				</div>
				{showCloseButton && (
					<button
						type="button"
						onClick={onClose}
						aria-label="Close"
						className="-mr-2 grid size-10 shrink-0 cursor-pointer place-items-center rounded-full transition-colors hover:bg-brand-soft"
					>
						<CloseIcon className="size-4.5" />
					</button>
				)}
			</div>

			<div className="px-6 py-5">{children}</div>
		</dialog>
	);
}
